<script setup>
// ============================================================================
// BonBoZ: TRANG CHINH — trang dap sau khi dang nhap, dung bo cuc Pancake.
//
// Danh sach trang dang noi, loc theo nen tang, cong hai modal: Ket noi (vo cho
// wizard inbox san co) va Gop trang (bo loc trang cho cot hoi thoai).
//
// Muon truc page thi bam "Hoi thoai" tren thanh tren.
// ============================================================================
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import Button from 'dashboard/components-next/button/Button.vue';
import MergePagesDialog from './MergePagesDialog.vue';
import PageCard from './PageCard.vue';
import TrangMatKetNoiDialog from './TrangMatKetNoiDialog.vue';
import { matKetNoi } from 'dashboard/helper/trangMatKetNoi';
import {
  ALL_PLATFORMS,
  buildPlatformTabs,
  filterPages,
  sortPinnedFirst,
} from './platforms';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isAdmin } = useAdmin();

const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

const tuKhoa = ref('');
const nenTang = ref(ALL_PLATFORMS);
const mergeDialog = ref(null);
const matKetNoiDialog = ref(null);

// Ghim trang: luu theo nguoi dung nhu moi lua chon giao dien khac.
const { uiSettings, updateUISettings } = useUISettings();
const { setSelectedInboxes } = useSelectedPages();
const daGhim = computed(() => uiSettings.value?.bonboz_pinned_inboxes ?? []);
const laGhim = inbox => daGhim.value.includes(Number(inbox.id));

const doiGhim = inbox => {
  const id = Number(inbox.id);
  const con = daGhim.value.filter(x => x !== id);
  updateUISettings({
    bonboz_pinned_inboxes: laGhim(inbox) ? con : [...con, id],
  });
};

const tabs = computed(() => buildPlatformTabs(inboxes.value));
const trangHien = computed(() =>
  sortPinnedFirst(
    filterPages(inboxes.value, nenTang.value, tuKhoa.value),
    daGhim.value
  )
);

const taiLai = () => store.dispatch('inboxes/get');

// BAM VAO MOT THE CHINH LA CHON TRANG. Ghi lai lua chon ngay tai day, truoc khi
// dieu huong: nhung man hinh KHONG co inbox tren duong dan — ro nhat la phan
// Thong ke — khong con cach nao khac de biet dang xem trang nao.
const moTrang = inbox => {
  // Trang mat ket noi: hoi truoc, khong co nut "Bo qua" vi bo xong la rong.
  if (matKetNoi(inbox)) {
    matKetNoiDialog.value.open({ trang: [inbox] });
    return;
  }
  setSelectedInboxes([inbox.id]);
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: accountId.value, inbox_id: inbox.id },
  });
};

const moCaiDat = inbox =>
  router.push({
    name: 'settings_inbox_show',
    params: { accountId: accountId.value, inboxId: inbox.id },
  });

// Danh sach trang co the chua nap khi vao thang bang URL.
onMounted(() => {
  if (!inboxes.value.length) taiLai();
});
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-slate-2">
    <!-- Le hai ben gian theo be ngang man hinh, tran o max-w de man rong khong
         keo mot hang the dai het co. -->
    <div
      class="flex flex-col w-full gap-3 px-4 py-4 mx-auto max-w-screen-2xl sm:px-6 lg:px-10 2xl:px-14 2xl:py-6"
    >
      <section class="p-4 bg-n-background rounded-xl 2xl:p-5">
        <h1 class="text-base font-semibold text-n-slate-12 2xl:text-lg">
          {{ t('BONBOZ.TRANG_CHINH.TIEU_DE') }}
        </h1>
        <div class="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div class="relative flex-1 min-w-[12rem] max-w-md">
            <!-- pointer-events-none: icon nam de len o nhap, khong tat thi bam
               trung kinh lup se khong focus duoc, go phim roi vao khoang khong
               va nguoi dung tuong bo loc hong. -->
            <span
              class="absolute pointer-events-none i-lucide-search size-4 text-n-slate-10 start-3 top-1/2 -translate-y-1/2"
            />
            <!-- reset-base: input toan cuc (_base.scss) co do uu tien rat cao,
               khong reset thi mat cho trong ben trai va tut khoi icon. -->
            <input
              v-model="tuKhoa"
              type="search"
              class="reset-base w-full !h-10 !m-0 !text-sm ps-9 pe-3 rounded-lg outline outline-1 outline-[rgb(var(--pk-vien))] bg-n-slate-2 text-n-slate-12 placeholder:text-n-slate-10 focus-visible:outline-2 focus-visible:outline-n-brand"
              :placeholder="t('BONBOZ.TRANG_CHINH.TIM_KIEM')"
            />
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              color="slate"
              icon="i-lucide-rotate-cw"
              :is-loading="uiFlags.isFetching"
              :aria-label="t('BONBOZ.TRANG_CHINH.TAI_LAI')"
              @click="taiLai"
            />
            <Button
              color="slate"
              icon="i-lucide-users"
              :label="t('BONBOZ.TRANG_CHINH.GOP_TRANG')"
              @click="mergeDialog.open()"
            />
          </div>
        </div>
      </section>

      <!-- Chi hien tab cho nen tang that su co inbox: mot tab rong khong noi len
         dieu gi ngoai viec no dang rong. -->
      <section
        v-if="tabs.length"
        class="flex items-center gap-1 p-2 overflow-x-auto bg-n-background rounded-xl"
        role="tablist"
      >
        <button
          v-for="tab in [
            {
              key: ALL_PLATFORMS,
              name: t('BONBOZ.TRANG_CHINH.TAT_CA'),
              icon: 'i-lucide-layers',
              count: inboxes.length,
            },
            ...tabs,
          ]"
          :key="tab.key"
          role="tab"
          :aria-selected="nenTang === tab.key"
          class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg shrink-0 transition-colors"
          :class="
            nenTang === tab.key
              ? 'bg-[rgb(var(--pk-hang-chon))] text-[rgb(var(--pk-tab-active))] font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="nenTang = tab.key"
        >
          <span :class="tab.icon" class="size-4 shrink-0" />
          {{ tab.name }}
          <span class="px-1.5 rounded-full text-xs tabular-nums bg-n-alpha-2">
            {{ tab.count }}
          </span>
        </button>
      </section>

      <section class="flex-1 p-4 bg-n-background rounded-xl 2xl:p-5">
        <div
          v-if="trangHien.length"
          class="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4"
        >
          <PageCard
            v-for="inbox in trangHien"
            :key="inbox.id"
            :inbox="inbox"
            @click="moTrang"
          >
            <template #actions>
              <Button
                v-tooltip.top="
                  laGhim(inbox)
                    ? t('BONBOZ.TRANG_CHINH.BO_GHIM')
                    : t('BONBOZ.TRANG_CHINH.GHIM')
                "
                variant="ghost"
                color="slate"
                :class="
                  laGhim(inbox)
                    ? '!bg-[rgb(var(--pk-ghim))] !text-[rgb(var(--pk-ghim-chu))]'
                    : ''
                "
                size="sm"
                icon="i-lucide-pin"
                :aria-pressed="laGhim(inbox)"
                :aria-label="
                  laGhim(inbox)
                    ? t('BONBOZ.TRANG_CHINH.BO_GHIM')
                    : t('BONBOZ.TRANG_CHINH.GHIM')
                "
                @click="doiGhim(inbox)"
              />
              <Button
                v-if="isAdmin"
                variant="ghost"
                color="slate"
                size="sm"
                icon="i-lucide-settings"
                :aria-label="t('BONBOZ.TRANG_CHINH.CAI_DAT_TRANG')"
                @click="moCaiDat(inbox)"
              />
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                icon="i-lucide-message-square"
                :aria-label="t('BONBOZ.TRANG_CHINH.XEM_HOI_THOAI')"
                @click="moTrang(inbox)"
              />
            </template>
          </PageCard>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ t('BONBOZ.TRANG_CHINH.TRONG') }}
        </p>
      </section>

      <MergePagesDialog
        ref="mergeDialog"
        :inboxes="inboxes"
        @applied="router.push({ name: 'home', params: { accountId } })"
      />
      <TrangMatKetNoiDialog ref="matKetNoiDialog" />
    </div>
  </div>
</template>
